import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy} from '@angular/core';
import {SortingCriterion} from '../../../../shared/models';
import {NgRedux} from '@angular-redux/store';
import {
  AppAction,
  multilevelTabChangedTo,
  NPVByDepartmentColumnsChangedTo,
  npvSnapshotSortingCriteriaChangedTo,
  patientVisitsCountsDisplayChangedTo
} from '../../../../store/actions';
import {BaseColumn, IAppState, INITIAL_STATE} from '../../../../store/IAppState';
import * as _ from 'lodash';
import {Page} from '../../../../shared/helpers';
import {DisplayField, Variable} from '../../../../variable-container/variable-container.component';
import {Router} from '@angular/router';
import {BenchmarkType, readableNameOfColumnDef} from '../../../../shared/benchmark-types';
import {AnalyticsService, AnalyticsServiceToken} from '../../../../analytics/analytics.service';
import {BenchmarkOption, ColumnType, MultilevelTab, SortingOrder} from '../../../../shared/enums';
import {DrillDownService} from '../../../../services/drilldown.service';
import {MergedNewPatientVisitSnapshotEntry, NewPatientVisitSnapshotMultiLevel} from '../../npv-models';
import {
  getDesignatedNpvBenchmarkObject,
  getDesignatedNpvVarianceObject,
  getIsDepartment,
  getIsSpecialty
} from '../../npv-helpers';

@Component({
  selector: 'app-new-patient-visits-department-page',
  templateUrl: './new-patient-visits-department-page.component.html',
  styleUrls: ['./new-patient-visits-department-page.component.scss']
})
export class NewPatientVisitsDepartmentPageComponent implements OnChanges, OnDestroy {
  level: string;
  snapshotNPVColumnsChangedTo = NPVByDepartmentColumnsChangedTo;
  defaultSortColumn = 'newPatientVisitsPercentage';
  isDepartment = false;
  isSpecialty = false;
  isProvider: boolean;
  sortingDataAccessor: any;

  variables: Variable[] = [{
    name: '# New and Total Patients',
    display: false,
    reducerField: DisplayField.PatientVisitCounts,
    dispatchAction(display: boolean): AppAction {
      return patientVisitsCountsDisplayChangedTo(display);
    }
  }];
  totals: NewPatientVisitSnapshotMultiLevel;

  @Input() fromPdfExport = false;
  @Input() benchmarkType: BenchmarkType;
  @Input() benchmarkOption: BenchmarkOption;
  @Input() viewCommunityBenchmarks: boolean;
  @Input() nodePath: string;
  @Input() isCustom: boolean;
  @Input() zeroSuppression = false;
  @Input() newPatientVisits: NewPatientVisitSnapshotMultiLevel[] = [];
  @Input() columns: any[];
  @Input() displayedColumns: any[];
  @Input() columnHeaders: BaseColumn[];
  @Input() countOfSuppressedEntries = 0;
  @Input() activeVarianceToggle = false;
  @Input() npvSortingCriteria: SortingCriterion | undefined = undefined;
  @Input() showExtendedBenchmarkOptions = false;
  MultilevelTab = MultilevelTab;
  zeroSuppressionCondition = 'a total patient count greater than zero';
  currentPage = Page.NPVSnapshot;
  varianceToggleSortingCriterion: SortingCriterion;
  showProgressBar = false;
  varianceKey = '';
  sortDirection: string;

  constructor(private ngRedux: NgRedux<IAppState>,
              private _changeDetectorRef: ChangeDetectorRef,
              private router: Router,
              private drillDownService: DrillDownService,
              @Inject(AnalyticsServiceToken) private readonly analyticsService: AnalyticsService) {
  }

  ngOnChanges() {
    this.showProgressBar = _.isEqual(this.newPatientVisits,
      INITIAL_STATE.data.newPatientVisitMultilevelData.departmentNpvSnapshotData);
    this.analyticsService.handleGoogleAnalytics('Npv Snapshot',
      this.viewCommunityBenchmarks ? 'Community' : 'Academic', 'Toggling Benchmark');
    this.sortingDataAccessor = (data: any, sortHeaderId: string) => {
      const asLower = sortHeaderId.toLowerCase();
      const readableName = readableNameOfColumnDef(this.benchmarkType);
      return (sortHeaderId === 'newPatientVisitsPercentage') ? data[this.defaultSortColumn] :
        asLower.includes('benchmark') ? data[getDesignatedNpvBenchmarkObject(this.benchmarkOption)]
          [`benchmark${readableName}`] : asLower.includes('variance')
          ? data[getDesignatedNpvVarianceObject(this.benchmarkOption)][`variance${readableName}`] :
          data[sortHeaderId];
    };
    this.benchmarkType = this.benchmarkType === BenchmarkType.Percentile65th ?
      BenchmarkType.Mean : this.benchmarkType;
    this.isSpecialty = getIsSpecialty(this.nodePath, this.isCustom);
    this.isDepartment = getIsDepartment(this.nodePath, this.isCustom);
    this._changeDetectorRef.detectChanges();
    const varianceColumn = this.columns.find((col: BaseColumn) => col.columnType === ColumnType.VARIANCE);
    this.varianceKey = varianceColumn?.columnDef || 'varianceMean';
    const npvSortColumn = 'newPatientVisitsPercentage';
    if (this.npvSortingCriteria && this.npvSortingCriteria.columnDef && this.npvSortingCriteria.sortingOrder) {
      const requiredSortingCriteria: SortingCriterion = this.npvSortingCriteria.columnType === ColumnType.VARIANCE ? {
        ...this.npvSortingCriteria,
        columnDef: this.varianceKey
      } : this.npvSortingCriteria;
      this.defaultSortColumn = requiredSortingCriteria.columnDef;
      this.sortDirection = this.npvSortingCriteria.sortingOrder === SortingOrder.DESCENDING ? 'desc' : 'asc';
    } else {
      this.defaultSortColumn = npvSortColumn;
      this.sortDirection = 'desc';
    }
    if (!this.npvSortingCriteria && ['asc', 'desc'].includes(this.sortDirection)) {
      this.ngRedux.dispatch(npvSnapshotSortingCriteriaChangedTo({
        sortingOrder: this.sortDirection === 'asc' ? SortingOrder.ASCENDING : SortingOrder.DESCENDING,
        columnDef: this.defaultSortColumn
      }));
    }
    if (!this.activeVarianceToggle) {
      this.varianceToggleSortingCriterion = {
        sortingOrder: SortingOrder.DESCENDING,
        columnDef: this.varianceKey,
        columnType: ColumnType.VARIANCE
      };
    } else {
      this.varianceToggleSortingCriterion = {
        sortingOrder: SortingOrder.DESCENDING,
        columnDef: npvSortColumn
      };
    }
  }

  ngOnDestroy() {
    this._changeDetectorRef.detach();
  }

  levelSpecificHandleSortChange = (newPatientVisitEntries: MergedNewPatientVisitSnapshotEntry[]) => {
    if (!_.isEqual(this.newPatientVisits, newPatientVisitEntries)) {
      this.newPatientVisits = newPatientVisitEntries;
    }
  };
  updateSortingCriteria = (sortingCriteria: SortingCriterion | undefined) => {
    this.ngRedux.dispatch(npvSnapshotSortingCriteriaChangedTo(sortingCriteria));
  };

  tieBreakerProperty = (newPatientVisit: NewPatientVisitSnapshotMultiLevel) => {
    return newPatientVisit.nodeName;
  };

  whenDepartmentSelected = (newPatientVisitEntry: any, table = true) => {
    if (!newPatientVisitEntry) {
      return;
    }
    this.analyticsService.handleGoogleAnalytics('Npv By Department',
      newPatientVisitEntry.nodeName, `Drill down on ${table ? 'Table' : 'Chart'}`);
    this.drillDownService.drillDownIntoNode(newPatientVisitEntry.nodePath, this.nodePath, MultilevelTab.BY_DEPARTMENT);
    this.ngRedux.dispatch(multilevelTabChangedTo(MultilevelTab.BY_SPECIALTY));
  };

  whenBarSelected = (nodePath: string) => {
    this.whenDepartmentSelected({
      nodeId: 0,
      nodeName: '',
      nodePath: nodePath
    }, false);
  };
}
